/**
 * MM Routine Resultat Interactions
 * Handles the dynamic interaction between routine product cards and the upsell grid
 */

class RoutineResultatManager {
    generateProductTitle(productElement) {
        // Extract metafields from the product element if available
        const shortTitle = productElement.querySelector('[data-short-title]')?.dataset.shortTitle;
        const contenance = productElement.querySelector('[data-contenance]')?.dataset.contenance;
        const productTitle = productElement.querySelector('[data-product-title]')?.dataset.productTitle;
        const variantTitle = productElement.querySelector('[data-variant-title]')?.dataset.variantTitle;
        
        if (shortTitle) {
            return `${shortTitle}${contenance ? ` - ${contenance}` : ''}`;
        } else {
            return `${productTitle}${variantTitle ? ` - ${variantTitle}` : ''}`;
        }
    }

    constructor() {
        this.grid = document.getElementById('routine-products-grid');
        this.toggleButtons = document.querySelectorAll('.mm-routine-resultat-toggle-product');
        this.maxSlots = parseInt(this.grid?.dataset.maxSlots) || 12;
        this.selectedProducts = new Map(); // uniqueId -> product data
        this.cartForm = document.getElementById('routine-cart-form');
        
        // Delivery settings from Liquid
        this.deliveryThreshold = parseInt(document.querySelector('[data-delivery-threshold]')?.dataset.deliveryThreshold) || 0;
        this.deliveryCost = parseInt(document.querySelector('[data-delivery-cost]')?.dataset.deliveryCost) || 0;
        this.freeText = document.querySelector('[data-free-text]')?.dataset.freeText || 'Gratuit';
        
        // Translation texts
        this.removedTextOne = document.querySelector('[data-removed-text-one]')?.dataset.removedTextOne || 'produit retiré de la routine';
        this.removedTextMany = document.querySelector('[data-removed-text-many]')?.dataset.removedTextMany || 'produits retirés de la routine';

        this.init();
    }

    init() {
        if (!this.grid) return;

        // Initialize with all products added by default
        this.initializeDefaultProducts();

        // Bind event listeners
        this.bindToggleButtons();
        this.bindGridRemoveButtons();
        this.bindProductSelectors();

        // Initial state update
        this.updateSummary();
        this.updatePricing();
        this.updateDelivery();
    }

    initializeDefaultProducts() {
        // Initialize from existing grid slots (from Liquid rendering)
        const existingSlots = this.grid.querySelectorAll('.mm-routine-resultat-upsell-product-slot[data-unique-id]');
        
        existingSlots.forEach(slot => {
            const uniqueId = slot.dataset.uniqueId;
            const productData = {
                uniqueId: uniqueId,
                variantId: slot.dataset.productId,
                title: slot.querySelector('img')?.alt || '',
                image: slot.querySelector('img')?.src || '',
                price: parseInt(slot.dataset.price || '0'),
                comparePrice: parseInt(slot.dataset.comparePrice || '0'),
                moment: slot.dataset.moment,
                action: slot.dataset.action,
                removed: slot.dataset.removed === 'true'
            };
            
            this.selectedProducts.set(uniqueId, productData);
        });
        
        // Update toggle buttons if they exist
        this.updateToggleButtonsFromGrid();
    }

    bindToggleButtons() {
        this.toggleButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                this.handleToggleProduct(button);
            });
        });
    }

    bindGridRemoveButtons() {
        // Use event delegation for dynamically rendered remove buttons
        this.grid.addEventListener('click', (e) => {
            const removeBtn = e.target.closest('.mm-routine-resultat-upsell-product-remove');
            if (removeBtn) {
                e.preventDefault();
                const slot = removeBtn.closest('.mm-routine-resultat-upsell-product-slot');
                const uniqueId = slot?.dataset.uniqueId;
                if (uniqueId) {
                    this.removeProductByUniqueId(uniqueId);
                }
            }
        });
    }

    bindProductSelectors() {
        // Listen for product selection events from the multi-product selector
        document.addEventListener('routineProductSelected', (e) => {
            this.handleSelectorProductChange(e.detail);
        });

        // Handle cart form submission
        if (this.cartForm) {
            this.cartForm.addEventListener('submit', (e) => {
                this.handleCartSubmit(e);
            });
        }
    }

    updateToggleButtonsFromGrid() {
        // Update toggle button states based on grid content
        this.toggleButtons.forEach(button => {
            const variantId = button.dataset.variantId;
            const hasVariantInGrid = Array.from(this.selectedProducts.values())
                .some(product => product.variantId === variantId && !product.removed);
            
            button.dataset.added = hasVariantInGrid ? 'true' : 'false';
            this.updateButtonState(button, hasVariantInGrid);
        });
    }

    handleToggleProduct(button) {
        const variantId = button.dataset.variantId;
        const isCurrentlyAdded = button.dataset.added === 'true';

        if (isCurrentlyAdded) {
            // Remove all instances of this product
            this.removeProduct(variantId);
        } else {
            // Re-add (restore) all instances of this product
            this.restoreProduct(variantId);
        }
    }

    handleSelectorProductChange(detail) {
        const { container } = detail;
        
        // Find the selector container to identify which products are available
        const allProducts = container.querySelectorAll('[data-product-selector]');
        const selectedProduct = container.querySelector('[data-product-selector].selected');
        
        if (!selectedProduct) return;
        
        const selectedVariantId = selectedProduct.dataset.variantId;
        
        // Find which product from this selector group is currently in the grid
        let currentProductInGrid = null;
        allProducts.forEach(productElement => {
            const productVariantId = productElement.dataset.variantId;
            const uniqueId = `upsell_${productVariantId}`;
            
            if (this.selectedProducts.has(uniqueId) && !this.selectedProducts.get(uniqueId).removed) {
                currentProductInGrid = { uniqueId, variantId: productVariantId, element: productElement };
            }
        });
        
        // If the selected product is already in the grid, do nothing
        if (currentProductInGrid && currentProductInGrid.variantId === selectedVariantId) {
            return;
        }
        
        // Replace the current product with the selected one
        if (currentProductInGrid) {
            // Replace existing product data without triggering removal
            this.replaceProductInGrid(currentProductInGrid.uniqueId, selectedVariantId, selectedProduct);
        } else {
            // No product from this selector in grid, add the selected one
            this.addProductToGrid(selectedVariantId, selectedProduct);
        }
    }

    syncWithGridState() {
        // Clear the current map and rebuild it from the actual grid state
        this.selectedProducts.clear();
        
        // Debug: Check grid element
        console.log('Grid element:', this.grid);
        
        // Get all product slots in the grid (including removed ones for debugging)
        const allSlots = this.grid.querySelectorAll('.mm-routine-resultat-upsell-product-slot');
        console.log('Total slots found:', allSlots.length);
        
        // Get all visible product slots in the grid
        const activeSlots = this.grid.querySelectorAll('.mm-routine-resultat-upsell-product-slot[data-removed="false"]');
        console.log('Active slots found:', activeSlots.length);
        
        // Also try without the data-removed filter to see what we get
        const slotsWithoutFilter = this.grid.querySelectorAll('.mm-routine-resultat-upsell-product-slot');
        console.log('Slots without filter:', slotsWithoutFilter.length);
        
        activeSlots.forEach((slot, index) => {
            const uniqueId = slot.dataset.uniqueId;
            const variantId = slot.dataset.productId;
            const titleElement = slot.querySelector('.mm-routine-resultat-upsell-product-name');
            const imgElement = slot.querySelector('.mm-routine-resultat-upsell-product-image');
            
            console.log(`Slot ${index}:`, {
                uniqueId,
                variantId,
                removed: slot.dataset.removed,
                hasTitle: !!titleElement,
                hasImage: !!imgElement,
                element: slot
            });
            
            if (uniqueId && variantId) {
                const productData = {
                    uniqueId: uniqueId,
                    variantId: variantId,
                    title: titleElement?.textContent || '',
                    image: imgElement?.src || '',
                    price: parseInt(slot.dataset.price) || 0,
                    comparePrice: parseInt(slot.dataset.comparePrice) || 0,
                    moment: '',
                    action: '',
                    removed: false
                };
                
                this.selectedProducts.set(uniqueId, productData);
                console.log(`Added product ${index} to map:`, productData);
            } else {
                console.log(`Skipped slot ${index} - missing uniqueId or variantId`);
            }
        });
        
        console.log('Synced with grid state - Found products:', this.selectedProducts.size);
        console.log('All products in map:', Array.from(this.selectedProducts.values()));
    }

    handleCartSubmit(e) {
        e.preventDefault(); // Prevent form submission
        
        // Synchronize with actual grid state before submitting
        this.syncWithGridState();
        
        // Check if there are products to add
        const activeProducts = Array.from(this.selectedProducts.values()).filter(p => !p.removed);
        console.log('Cart submit - Active products found:', activeProducts.length);
        
        if (activeProducts.length === 0) {
            console.warn('No products selected for cart');
            return;
        }

        // Count variants and prepare items array for bulk add
        const variantCounts = new Map();
        for (const product of activeProducts) {
            const currentCount = variantCounts.get(product.variantId) || 0;
            variantCounts.set(product.variantId, currentCount + 1);
        }

        // Create items array for Shopify cart API
        const cartItems = Array.from(variantCounts.entries()).map(([variantId, quantity]) => ({
            id: variantId,
            quantity: quantity
        }));

        console.log('Adding items to cart:', cartItems);

        // Add all products in one API call using the same pattern as mmAddProductToCart
        if (typeof mmAPIRequest === 'function') {
            mmAPIRequest("POST", "cart/add.js", {
                items: cartItems,
                sections: mmCartSectionId(),
            })
            .then(refreshCartFromData)
            .then(mmAddOrRemoveFreeProducts)
            .then(() => {
                setTimeout(mmOpenCart);
            })
            .catch((error) => {
                alert(error.message);
                mmOpenCart();
                console.log(error);
            });
        } else {
            console.error('mmAPIRequest function not available');
        }
    }

    replaceProductInGrid(oldUniqueId, newVariantId, newProductElement) {
        const newUniqueId = `upsell_${newVariantId}`;
        
        // Get the existing slot DOM element
        const existingSlot = this.grid.querySelector(`[data-unique-id="${oldUniqueId}"]`);
        if (!existingSlot) return;
        
        // Extract new product data from the selector element
        const button = newProductElement.querySelector('.mm-routine-resultat-selector-product-btn');
        const productTitle = button?.dataset.productTitle || 'Produit';
        const productImage = button?.dataset.productImage || '';
        const price = parseInt(button?.dataset.price) || 0;
        const comparePrice = parseInt(button?.dataset.comparePrice) || price;
        
        // Update DOM element
        existingSlot.dataset.uniqueId = newUniqueId;
        existingSlot.dataset.productId = newVariantId;
        existingSlot.dataset.removed = 'false'; // Ensure slot is not marked as removed
        
        // Update image and title
        const img = existingSlot.querySelector('.mm-routine-resultat-upsell-product-image');
        const title = existingSlot.querySelector('.mm-routine-resultat-upsell-product-name');
        
        
        if (img && productImage) {
            // Handle both img tags and svg placeholders
            if (img.tagName === 'IMG') {
                // Use the image URL as-is (already absolute from Liquid filter)
                const imageUrl = productImage;
                
                // Force complete image replacement to avoid caching issues
                const newImg = document.createElement('img');
                newImg.className = 'mm-routine-resultat-upsell-product-image';
                newImg.src = imageUrl;
                newImg.alt = productTitle;
                
                // Replace the old image with the new one
                img.parentNode.replaceChild(newImg, img);
            } else if (img.tagName === 'svg') {
                // Replace SVG placeholder with actual image  
                const imageUrl = productImage;
                const newImg = document.createElement('img');
                newImg.className = 'mm-routine-resultat-upsell-product-image';
                newImg.src = imageUrl;
                newImg.alt = productTitle;
                img.parentNode.replaceChild(newImg, img);
            }
        }
        
        if (title) {
            title.textContent = this.generateProductTitle(newProductElement);
        }
        
        // Update internal data structure
        this.selectedProducts.delete(oldUniqueId);
        
        const newProductData = {
            uniqueId: newUniqueId,
            variantId: newVariantId,
            title: productTitle,
            image: productImage,
            price: price,
            comparePrice: comparePrice,
            moment: '',
            action: '',
            removed: false
        };
        
        this.selectedProducts.set(newUniqueId, newProductData);
        
        // Update components without showing removal message
        this.updateToggleButtonsFromGrid();
        this.updateSummary();
        this.updatePricing();
        this.updateDelivery();
        // Note: No reorganizeGrid() call to avoid triggering animations
    }

    addProductToGrid(variantId, productElement) {
        const uniqueId = `upsell_${variantId}`;
        
        // Check if product already exists in the grid
        let existingSlot = this.grid.querySelector(`[data-unique-id="${uniqueId}"]`);
        
        if (existingSlot) {
            // Product exists, just restore it
            this.restoreProduct(variantId);
        } else {
            // Product doesn't exist, create new slot and add at the end
            this.createNewProductSlot(variantId, productElement);
        }
    }

    createNewProductSlot(variantId, productElement) {
        const uniqueId = `upsell_${variantId}`;
        
        // Extract product data from the selector element's button
        const button = productElement.querySelector('.mm-routine-resultat-selector-product-btn');
        const productTitle = button?.dataset.productTitle || 'Produit';
        const productImage = button?.dataset.productImage || '';
        
        // Get next slot number
        const existingSlots = this.grid.querySelectorAll('.mm-routine-resultat-upsell-product-slot');
        const nextSlotNumber = existingSlots.length + 1;
        
        // Create new slot from template
        const template = document.getElementById('mm-routine-resultat-upsell-product-template');
        if (!template) {
            console.error('Template not found');
            return;
        }
        
        const newSlot = template.content.firstElementChild.cloneNode(true);
        newSlot.dataset.slot = nextSlotNumber;
        newSlot.dataset.productId = variantId;
        newSlot.dataset.uniqueId = uniqueId;
        newSlot.dataset.removed = 'false';
        newSlot.dataset.price = '0'; // Price will be updated by pricing logic
        newSlot.dataset.comparePrice = '0';
        
        // Update image and title from template
        const img = newSlot.querySelector('.mm-routine-resultat-upsell-product-image');
        const title = newSlot.querySelector('.mm-routine-resultat-upsell-product-name');
        
        if (img && productImage) {
            img.src = productImage;
            img.alt = productTitle;
        }
        
        if (title) {
            title.textContent = this.generateProductTitle(productElement);
        }
        
        // Add to selected products
        const productData = {
            uniqueId: uniqueId,
            variantId: variantId,
            title: productTitle,
            image: productImage,
            price: 0, // Will be updated
            comparePrice: 0, // Will be updated
            moment: '',
            action: '',
            removed: false
        };
        
        this.selectedProducts.set(uniqueId, productData);
        
        // Insert before any removed slots
        const removedSlots = this.grid.querySelectorAll('[data-removed="true"]');
        if (removedSlots.length > 0) {
            this.grid.insertBefore(newSlot, removedSlots[0]);
        } else {
            this.grid.appendChild(newSlot);
        }
        
        this.updateAllComponents();
    }

    removeProductByUniqueId(uniqueId) {
        const product = this.selectedProducts.get(uniqueId);
        if (!product) return;

        // Mark as removed instead of deleting to preserve the slot
        product.removed = true;
        this.selectedProducts.set(uniqueId, product);

        // Update the DOM slot
        const slot = this.grid.querySelector(`[data-unique-id="${uniqueId}"]`);
        if (slot) {
            slot.dataset.removed = 'true';
        }

        // Update selector state if this product was from a selector
        this.updateSelectorStateOnRemoval(product.variantId);
        
        this.updateAllComponents();

        // Dispatch custom event
        this.dispatchRemovalEvent(uniqueId, product);
    }

    updateSelectorStateOnRemoval(variantId) {
        // Find all selector products with this variant ID and deselect them
        const selectorProducts = document.querySelectorAll(`[data-product-selector][data-variant-id="${variantId}"]`);
        
        selectorProducts.forEach(selectorProduct => {
            // Only update if currently selected
            if (selectorProduct.classList.contains('selected')) {
                selectorProduct.classList.remove('selected');
                
                const button = selectorProduct.querySelector('.mm-routine-resultat-selector-product-btn');
                const buttonText = button?.querySelector('.mm-routine-resultat-selector-product-btn-text');
                const iconContainer = button?.querySelector('.mm-routine-resultat-card-btn-icon-container');
                
                if (button) {
                    button.classList.remove('mm-routine-resultat-card-btn-added');
                    button.dataset.added = 'false';
                }
                
                if (buttonText) {
                    buttonText.textContent = buttonText.dataset.textAdd;
                }
                
                if (iconContainer) {
                    iconContainer.style.display = 'none';
                }
            }
        });
    }

    removeProduct(variantId) {
        const uniqueId = `upsell_${variantId}`;
        this.removeProductByUniqueId(uniqueId);
    }

    restoreProduct(variantId) {
        const uniqueId = `upsell_${variantId}`;
        const product = this.selectedProducts.get(uniqueId);
        
        if (product && product.removed) {
            product.removed = false;
            this.selectedProducts.set(uniqueId, product);

            const slot = this.grid.querySelector(`[data-unique-id="${uniqueId}"]`);
            if (slot) {
                slot.dataset.removed = 'false';
            }

            this.updateAllComponents();
            this.dispatchRestoreEvent(variantId);
        }
    }

    updateAllComponents() {
        this.updateToggleButtonsFromGrid();
        this.updateSummary();
        this.updatePricing();
        this.updateDelivery();
        this.reorganizeGrid();
    }

    extractProductData(button) {
        return {
            variantId: button.dataset.variantId,
            title: button.dataset.productTitle,
            variantTitle: button.dataset.variantTitle,
            image: button.dataset.productImage
        };
    }

    reorganizeGrid() {
        if (!this.grid) return;

        // Get all slots in the grid
        const allSlots = Array.from(this.grid.querySelectorAll('.mm-routine-resultat-upsell-product-slot'));
        
        // Separate active and removed slots
        const activeSlots = allSlots.filter(slot => slot.dataset.removed !== 'true');
        const removedSlots = allSlots.filter(slot => slot.dataset.removed === 'true');
        
        // Sort active slots by their original position to maintain order
        activeSlots.sort((a, b) => {
            const slotA = parseInt(a.dataset.slot) || 0;
            const slotB = parseInt(b.dataset.slot) || 0;
            return slotA - slotB;
        });

        // Reorder DOM elements: active slots first, then removed slots
        activeSlots.forEach(slot => {
            this.grid.appendChild(slot);
        });
        
        removedSlots.forEach(slot => {
            this.grid.appendChild(slot);
        });

        // Update visual indicators for empty spaces
        this.updateGridEmptySlots(activeSlots.length);
    }

    updateGridEmptySlots(activeCount) {
        // Remove existing empty slots
        const existingEmptySlots = this.grid.querySelectorAll('.mm-routine-resultat-upsell-empty-slot');
        existingEmptySlots.forEach(slot => slot.remove());

        // Add empty slots to fill the grid (assuming 12 max slots)
        const maxSlots = this.maxSlots || 12;
        const emptySlotsNeeded = Math.max(0, maxSlots - activeCount);
        
        for (let i = 0; i < emptySlotsNeeded; i++) {
            const emptySlot = document.createElement('div');
            emptySlot.className = 'mm-routine-resultat-upsell-empty-slot';
            emptySlot.innerHTML = '<div class="mm-routine-resultat-upsell-empty-card"></div>';
            this.grid.appendChild(emptySlot);
        }
    }

    updateButtonState(button, isAdded) {
        const textSpan = button.querySelector('.mm-routine-resultat-card-btn-text');
        const iconElement = button.querySelector('.mm-routine-resultat-card-btn-icon');

        if (isAdded) {
            textSpan.textContent = textSpan.dataset.textAdded || 'Ajouté';
            if (iconElement) iconElement.style.display = 'inline-block';
            button.classList.add('mm-routine-resultat-card-btn-added');
        } else {
            textSpan.textContent = textSpan.dataset.textAdd || 'Ajouter';
            if (iconElement) iconElement.style.display = 'none';
            button.classList.remove('mm-routine-resultat-card-btn-added');
        }
    }

    updateSummary() {
        // Count unique active products for display
        const uniqueActiveProducts = new Set();
        for (const product of this.selectedProducts.values()) {
            if (!product.removed) {
                uniqueActiveProducts.add(product.variantId);
            }
        }

        // Update product count in header
        const countElement = document.getElementById('routine-products-count');
        if (countElement) {
            const count = uniqueActiveProducts.size;
            const productText = count <= 1 ? 'produit' : 'produits';
            countElement.textContent = `${count} ${productText}`;
        }

        // Update removed products notice
        const removedCount = Array.from(this.selectedProducts.values()).filter(p => p.removed).length;
        
        const removedCountElement = document.getElementById('removed-count');
        const removedTextElement = document.getElementById('removed-text');
        const removedSection = document.querySelector('.mm-routine-resultat-upsell-removed');

        if (removedCountElement && removedSection) {
            removedCountElement.textContent = removedCount;
            
            // Update text based on count
            if (removedTextElement) {
                if (removedCount <= 1) {
                    removedTextElement.textContent = this.removedTextOne;
                } else {
                    removedTextElement.textContent = this.removedTextMany;
                }
            }
            
            if (removedCount > 0) {
                removedSection.style.display = 'block';
                removedSection.dataset.count = removedCount;
            } else {
                removedSection.style.display = 'none';
            }
        }

        // Update add to cart button state
        this.updateAddToCartButton(uniqueActiveProducts.size);
    }

    updateAddToCartButton(activeProductCount) {
        const addToCartButton = document.querySelector('.mm-routine-resultat-upsell-button');
        
        if (addToCartButton) {
            if (activeProductCount === 0) {
                // No products active - disable button
                addToCartButton.disabled = true;
                addToCartButton.setAttribute('aria-disabled', 'true');
                addToCartButton.classList.add('mm-routine-resultat-upsell-button-disabled');
            } else {
                // Products active - enable button
                addToCartButton.disabled = false;
                addToCartButton.removeAttribute('aria-disabled');
                addToCartButton.classList.remove('mm-routine-resultat-upsell-button-disabled');
            }
        }
    }

    updatePricing() {
        // Calculate total price from unique active products only
        const uniqueProducts = new Set();
        let totalPrice = 0;
        let totalComparePrice = 0;

        for (const product of this.selectedProducts.values()) {
            if (!product.removed && !uniqueProducts.has(product.variantId)) {
                uniqueProducts.add(product.variantId);
                
                // Ensure valid numbers to prevent NaN
                const price = parseInt(product.price) || 0;
                const comparePrice = parseInt(product.comparePrice) || price;
                
                
                totalPrice += price;
                totalComparePrice += comparePrice;
            }
        }
        
        // Update price displays
        const priceElement = document.getElementById('routine-total-price');
        const oldPriceElement = document.getElementById('routine-old-price');
        const discountElement = document.getElementById('routine-discount');

        if (priceElement) {
    const diagnosticDiscount = 0.15;
    const discountedPrice = Math.round(totalPrice * (1 - diagnosticDiscount));

    priceElement.textContent = this.formatPrice(discountedPrice);

    if (oldPriceElement) {
        oldPriceElement.textContent = this.formatPrice(totalPrice);
        oldPriceElement.style.display = 'block';
    }

    if (discountElement) {
        discountElement.textContent = `-15% ${diagnosticDiscountCode}`;
        discountElement.style.display = 'flex';
    }
}


        return totalPrice;
    }


    updateDelivery() {
        const totalPrice = this.updatePricing();
        
        // Get the delivery price element
        const priceElement = document.querySelector('.mm-routine-resultat-upsell-delivery-price');
        
        if (!priceElement) return;

        if (this.deliveryThreshold > 0) {
            if (totalPrice >= this.deliveryThreshold) {
                // Free delivery: show free text
                const freeText = priceElement.dataset.deliveryFreeText || 'Gratuit';
                priceElement.textContent = freeText;
            } else {
                // Paid delivery: show delivery cost
                const deliveryCost = priceElement.dataset.deliveryCost || 'Prix non défini';
                priceElement.textContent = deliveryCost;
            }
        } else {
            // No threshold: show fallback text
            const fallbackText = priceElement.dataset.deliveryFallback || 'Offerte';
            priceElement.textContent = fallbackText;
        }
    }

    formatPrice(centimes) {
        const euros = centimes / 100;
        return euros.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
    }

    dispatchRemovalEvent(uniqueId, product) {
        const event = new CustomEvent('routineProductRemoved', {
            detail: {
                uniqueId: uniqueId,
                variantId: product.variantId,
                moment: product.moment,
                action: product.action,
                removedCount: Array.from(this.selectedProducts.values()).filter(p => p.removed).length
            }
        });
        document.dispatchEvent(event);
    }

    dispatchRestoreEvent(variantId) {
        const event = new CustomEvent('routineProductRestored', {
            detail: {
                variantId: variantId,
                removedCount: Array.from(this.selectedProducts.values()).filter(p => p.removed).length
            }
        });
        document.dispatchEvent(event);
    }

    showGridFullMessage() {
        // Show a temporary message that the grid is full
        console.warn('La grille est pleine. Maximum ' + this.maxSlots + ' produits autorisés.');
        // You could show a toast notification here
    }
}

// Initialize Splide carousel for reassurance
function initReassuranceCarousel() {
    const splideElem = document.querySelector('.mm-routine-reassurance-splide');
    if (splideElem) {
        new Splide(splideElem, {
            type: "loop",
            autoplay: true,
            rewind: true,
            arrows: false,
            pagination: true,
            perPage: 1,
            interval: 3000
        }).mount();
    }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    window.routineManager = new RoutineResultatManager();
    initReassuranceCarousel();
});